import React from "react";
import { motion } from "framer-motion";
import portrait from "../assets/aboutphoto.jpg";

const highlights = [
  {
    title: "Payments & commerce",
    copy: "Razorpay checkout flows, multi-vendor order splitting across 10+ vendors, and Decentro KYC onboarding for live B2B and B2C operations.",
  },
  {
    title: "Reliability engineering",
    copy: "Idempotent webhook pipelines with database-level locking and a custom PostgreSQL job queue, holding order-event stability near 99%.",
  },
  {
    title: "Full stack delivery",
    copy: "React, Next.js, React Native, Node.js, Express, PostgreSQL, and AWS — owning features from interface to deployment.",
  },
  {
    title: "Operational ownership",
    copy: "EC2 setup, EventBridge scheduling, Nginx, Google Play releases, and hands-on troubleshooting when things break.",
  },
];

const About = () => {
  return (
    <section name="about" className="section-frame">
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="space-y-8"
        >
          <div>
            <p className="section-kicker">About</p>
            <h2 className="section-title mt-6 max-w-xl">
              Building thoughtful digital work with equal attention to detail
              and delivery.
            </h2>
          </div>

          <p className="section-copy text-slate-300">
            I&apos;m Shubham, a full stack developer who works on the backbone of
            commerce apps — the payment flows, order systems, and reliability
            layer products depend on. I care less about screens that merely look
            modern and more about systems that stay correct under real business
            load.
          </p>

          <p className="section-copy text-slate-300">
            I started at PureCode in Hyderabad on frontend design, reusable React
            components, and authentication flows, then joined Vadhando Easybiznus
            in August 2024. Across the Easybiznus app and QRPayIndia I&apos;ve
            delivered Razorpay checkout flows, idempotent Shopify webhook
            pipelines, multi-vendor order splitting, and a custom job queue for
            B2B and B2C use cases.
          </p>

          <a
            href="/resume.html"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-full border border-white/14 px-5 py-3 text-sm font-semibold uppercase tracking-[0.22em] text-slate-100 transition hover:border-[#f59e0b]/70 hover:text-white"
          >
            Download CV
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.75, delay: 0.1 }}
          className="grid gap-8"
        >
          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b1220]">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
              <img
                src={portrait}
                alt="Shubham at work"
                className="h-full min-h-[340px] w-full object-cover object-top"
              />

              <div className="p-8 sm:p-10">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">
                  Snapshot
                </p>
                <p className="mt-5 text-2xl font-semibold leading-snug text-slate-50">
                  I build the payment, order, and reliability systems that keep
                  production commerce products running — not just the screens on
                  top of them.
                </p>
                <div className="mt-8 grid gap-5 border-t border-white/10 pt-6 sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-500">
                      Education
                    </p>
                    <p className="mt-2 text-sm text-slate-200">
                      Master&apos;s in Computer Applications
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-500">
                      Experience
                    </p>
                    <p className="mt-2 text-sm text-slate-200">
                      Full Stack Developer at Easybiznus
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="border-t border-white/10 pt-5"
              >
                <p className="font-['Sora'] text-lg text-slate-100">
                  {item.title}
                </p>
                <p className="mt-3 text-sm leading-7 text-slate-300">
                  {item.copy}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
